import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Subtitle,
  HeaderContainer,
  Products,
  Price,
  Label,
  Quantity,
  Name,
  Column,
  Product,
  ProductImg,
  LoaderContainer,
  Chevron,
  DeliveryDetailsSection,
  ContactDetailsContainer,
  ContactItem,
  ContactValue,
  LocationName,
  AddressDetails,
  MapContainer,
  ContactMapContainer,
  SectionTitle,
  ContactPhone,
} from './DeliveryDetailsPage.styles.js';

import MainContainer from '../../components/utils/MainContainer/MainContainer.js';
import MiniLoader from '../../components/MiniLoader/MiniLoader.js';

import { ReactComponent as Loading } from '../../assets/icons/loader-white.svg';

import openToast from '../../utils/toast.js';
import { priceFormatter } from '../../utils/utils.js';

import { fetchDelivery } from '../../store/slices/delivery/asyncThunks.js';
import { selectFetchDeliveryPending, selectSelectedDelivery } from '../../store/slices/delivery/slice.js';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { DEFAULT_LATITUDE, DEFAULT_LONGITUDE, googlePlacesKey, MAP_LIBRARIES, MAP_ZOOM_LEVEL } from '../../utils/constants.js';

const DeliveryDetailsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const toastId = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const data = useSelector(selectSelectedDelivery);
  const loading = useSelector(selectFetchDeliveryPending);

  useEffect(() => {
    dispatch(fetchDelivery(id))
      .unwrap()
      .then(() => {})
      .catch(err => openToast(toastId, 'error-get', 'error', err.message));
  }, [dispatch, id]);

  const mapRef = useRef();
  const [mapZoom, setMapZoom] = useState(MAP_ZOOM_LEVEL);
  const [deliveryLocation, setDeliveryLocation] = useState('');

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googlePlacesKey,
    libraries: MAP_LIBRARIES,
  });

  const center =
    data?.latitude && data?.longitude
      ? {
          lat: data.latitude,
          lng: data.longitude,
        }
      : {
          lat: DEFAULT_LATITUDE,
          lng: DEFAULT_LONGITUDE,
        };

  const handleMarkerDragEnd = event => {
    const { latLng } = event;
    const lat = latLng.lat();
    const lng = latLng.lng();
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results?.length > 0) {
        const address = results[0].formatted_address;
        setDeliveryLocation({ lat, lng, name: address });
      }
    });
  };

  const handleMapClick = event => {
    const { latLng } = event;
    const lat = latLng.lat();
    const lng = latLng.lng();
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results?.length > 0) {
        const address = results[0].formatted_address;
        setDeliveryLocation({ lat, lng, name: address });
      }
    });
  };

  const onLoad = useCallback(
    function callback(map) {
      mapRef.current = map;
      map.setCenter(center);
      map.setZoom(MAP_ZOOM_LEVEL);
    },
    [center],
  );

  return (
    <MainContainer pageTitle={'delivery-details'}>
      {loading ? (
        <LoaderContainer>
          <MiniLoader />
        </LoaderContainer>
      ) : (
        <>
          <HeaderContainer>
            <Chevron
              onClick={() => {
                navigate(-1);
              }}
            />
            <Subtitle>
              {t('delivery')}
              {`#${data.id}`}
            </Subtitle>
          </HeaderContainer>
          <Products>
            {data?.items?.map((product, i) => (
              <Product key={i}>
                <ProductImg src={product?.photos[0]?.url} />
                <Column>
                  <SectionTitle>{`${t('product-details')}:`}</SectionTitle>
                  <Name>{product?.name}</Name>
                  <Price>{priceFormatter(product?.price)} SR</Price>
                </Column>
                <Column>
                  <SectionTitle>{`${t('quantity')}:`}</SectionTitle>
                  <Quantity>{product?.quantity}</Quantity>
                </Column>
                <Column>
                  <SectionTitle>{t('total')}</SectionTitle>
                  <Price>{priceFormatter(product?.price * product?.quantity)} SR</Price>
                </Column>
              </Product>
            ))}
          </Products>
        </>
      )}

      <DeliveryDetailsSection>
        <SectionTitle>{t('contact-details')}</SectionTitle>

        <ContactMapContainer>
          <ContactDetailsContainer>
            {data?.fullName && <ContactValue>{data?.fullName}</ContactValue>}

            <ContactItem>
              <ContactPhone>{data?.customerDetails?.phoneNumber || data?.phone}</ContactPhone>
            </ContactItem>
          </ContactDetailsContainer>

          <MapContainer>
            <SectionTitle>{t('delivery-location')}</SectionTitle>
            {data?.addressName && <LocationName>{data?.addressName}</LocationName>}

            {isLoaded ? (
              <GoogleMap
                mapContainerClassName="map"
                mapContainerStyle={{
                  border: '1px solid white',
                  width: '100%',
                  maxWidth: 400,
                  height: 400,
                  borderRadius: 10,
                  boxShadow: 'rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px',
                }}
                onLoad={map => {
                  mapRef.current = map;
                  onLoad(map);
                }}
                zoom={mapZoom}
                onClick={handleMapClick}>
                <Marker
                  position={
                    data?.latitude && data?.longitude
                      ? {
                          lat: data.latitude,
                          lng: data.longitude,
                        }
                      : {
                          lat: DEFAULT_LATITUDE,
                          lng: DEFAULT_LONGITUDE,
                        }
                  }
                  draggable
                  onDragEnd={handleMarkerDragEnd}
                />
              </GoogleMap>
            ) : (
              <div style={{ marginTop: 50 }}>
                <Loading />
              </div>
            )}
            <AddressDetails>
              <SectionTitle>{t('address-details')}</SectionTitle>
              {data?.addressDetails && <LocationName>{data?.addressDetails}</LocationName>}
            </AddressDetails>
          </MapContainer>
        </ContactMapContainer>
      </DeliveryDetailsSection>
    </MainContainer>
  );
};

export default DeliveryDetailsPage;
